import type { BottomTabBarProps } from "expo-router/tabs";
import { useEffect, useState } from "react";
import { Animated, Platform, StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { easeOutExpo, useReducedMotion } from "@/ui/motion";
import { useTheme } from "@/theme/useTheme";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { parentSections } from "./sections";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    bar: {
      flexDirection: "row",
      backgroundColor: t.color.background,
      borderTopWidth: t.borderWidth.card,
      borderTopColor: t.color.border,
      paddingTop: t.space.xs,
      paddingHorizontal: t.space.xs,
    },
    list: { flex: 1, flexDirection: "row", gap: t.space.xs },
    // Rounded cells; the current one is a filled blue tile that eases in.
    tab: {
      flex: 1,
      gap: t.space.xxs,
      paddingVertical: t.space.xs,
      borderRadius: t.radius.button,
      overflow: "hidden",
    },
    fill: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: t.radius.button,
      backgroundColor: t.color.tileBlue,
    },
    label: { textTransform: "lowercase" },
  }),
);

/** The current tab's blue cell: grows and fades in (or out) as the selection moves. */
function ActiveFill({ on, style }: { on: boolean; style: object }) {
  const reduced = useReducedMotion();
  const [level] = useState(() => new Animated.Value(on ? 1 : 0));
  useEffect(() => {
    Animated.timing(level, {
      toValue: on ? 1 : 0,
      duration: reduced ? 120 : 280,
      easing: easeOutExpo,
      useNativeDriver: Platform.OS !== "web",
    }).start();
  }, [on, level, reduced]);
  const scale = level.interpolate({
    inputRange: [0, 1],
    outputRange: [0.8, 1],
  });
  return (
    <Animated.View
      pointerEvents="none"
      style={[style, { opacity: level, transform: reduced ? [] : [{ scale }] }]}
    />
  );
}

/**
 * Bottom tabs Home / Shop / Orders / Profile for the mobile chrome (FR-005, FR-044; board MHome):
 * icon and label, the current tab marked selected, 44 px targets, role "tab".
 * Used as the `tabBar` of the `(parent)/(tabs)` navigator.
 */
export function MobileTabBar({ state, navigation, insets }: BottomTabBarProps) {
  const theme = useTheme();
  const styles = useStyles();
  const currentName = state.routes[state.index]?.name;

  return (
    <View
      style={[
        styles.bar,
        { paddingBottom: Math.max(insets.bottom, theme.space.xs) },
      ]}
      role="navigation"
      aria-label="Sections"
      testID="mobile-tab-bar"
    >
      <View role="tablist" style={styles.list}>
        {parentSections.map((section) => {
          const route = state.routes.find((r) => r.name === section.name);
          const selected = section.name === currentName;
          const color = selected ? theme.color.onTile : theme.color.textMuted;
          const onPress = () => {
            if (!route) return;
            const event = navigation.emit({
              type: "tabPress",
              target: route.key,
              canPreventDefault: true,
            });
            if (!selected && !event.defaultPrevented)
              navigation.navigate(route.name);
          };
          return (
            <PressableTarget
              key={section.name}
              accessibilityRole="tab"
              accessibilityLabel={section.label}
              aria-selected={selected}
              onPress={onPress}
              style={styles.tab}
            >
              <ActiveFill on={selected} style={styles.fill} />
              {/* A View keeps the icon stacked above the animated fill on the web. */}
              <View>
                <section.Icon
                  color={color}
                  size={theme.size.icon.md}
                  testID={`tab-icon-${section.label}`}
                />
              </View>
              <Text
                size="xs"
                weight="semiBold"
                style={[styles.label, { color }]}
              >
                {section.label}
              </Text>
            </PressableTarget>
          );
        })}
      </View>
    </View>
  );
}
